import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, BarChart3, DollarSign, Shield, Globe, Zap } from 'lucide-react';

// Import professional stock trading image
const stockTradingImage = require('../../assets/man-is-holding-tablet-with-graph-him_479259-14595.jpg_dz4zsm.jpg');
const professionalTradingImage = require('../../assets/two-stock-exchange-traders-discussing-dynamic-investment-graph-sellable_31965-313820.jpg_yeupoj.jpg');

const StockTrading = () => {
  const stockFeatures = [
    {
      icon: <TrendingUp className="h-8 w-8 text-emerald-600" />,
      title: 'Real-Time Market Data',
      description: 'Access live stock prices from major exchanges worldwide with millisecond precision.',
      benefits: ['Live NYSE & NASDAQ feeds', 'Level 2 market data', 'After-hours trading', 'Pre-market analysis']
    },
    {
      icon: <BarChart3 className="h-8 w-8 text-blue-600" />,
      title: 'Advanced Analytics',
      description: 'Professional-grade charts and technical analysis tools for informed trading decisions.',
      benefits: ['100+ technical indicators', 'Pattern recognition', 'Custom screeners', 'Risk analysis']
    },
    {
      icon: <DollarSign className="h-8 w-8 text-purple-600" />,
      title: 'Zero Commission',
      description: 'Trade US stocks and ETFs with zero commission fees. Keep more of your profits.',
      benefits: ['$0 stock trades', 'No account minimums', 'Free dividend reinvestment', 'Competitive margins']
    },
    {
      icon: <Shield className="h-8 w-8 text-orange-600" />,
      title: 'Secure Trading',
      description: 'Your investments are protected with SIPC insurance and bank-grade security.',
      benefits: ['SIPC protection', '256-bit encryption', 'Two-factor authentication', 'Cold storage']
    }
  ];

  const popularStocks = [
    { symbol: 'AAPL', name: 'Apple Inc.', price: '$175.43', change: '+2.15%', sector: 'Technology' },
    { symbol: 'MSFT', name: 'Microsoft Corp.', price: '$378.85', change: '+1.28%', sector: 'Technology' },
    { symbol: 'GOOGL', name: 'Alphabet Inc.', price: '$142.56', change: '+3.45%', sector: 'Technology' },
    { symbol: 'AMZN', name: 'Amazon.com Inc.', price: '$145.67', change: '-0.85%', sector: 'Consumer' },
    { symbol: 'TSLA', name: 'Tesla Inc.', price: '$245.67', change: '+4.32%', sector: 'Automotive' },
    { symbol: 'NVDA', name: 'NVIDIA Corp.', price: '$489.23', change: '+5.67%', sector: 'Technology' }
  ];

  return (
    <div className="stock-trading-page">
      {/* Hero Section */}
      <section className="stock-trading-hero">
        <div className="stock-trading-hero-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="stock-trading-hero-content"
            >
              <div className="stock-trading-hero-badge">
                <TrendingUp className="stock-trading-hero-badge-icon" />
                <span>Stock Trading</span>
              </div>
              <h1 className="stock-trading-hero-title">
                Trade Stocks with
                <span className="stock-trading-hero-title-gradient"> Zero Commission</span>
              </h1>
              <p className="stock-trading-hero-subtitle">
                Access thousands of stocks from major exchanges worldwide. Trade with confidence using our 
                professional-grade platform with real-time data and advanced analytics.
              </p>
              <div className="stock-trading-hero-buttons">
                <motion.a
                  href="https://dashboard.chartlinebrokerage.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="stock-trading-hero-cta-primary"
                >
                  Start Stock Trading
                </motion.a>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="stock-trading-hero-cta-secondary"
                >
                  View Demo
                </motion.button>
              </div>
            </motion.div>

            {/* Right Image */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="stock-trading-hero-image"
            >
              <div className="relative">
                <img
                  src={stockTradingImage}
                  alt="Stock Trading Platform"
                  className="w-full h-auto rounded-2xl shadow-2xl"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/20 to-transparent rounded-2xl" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="stock-trading-features-section">
        <div className="stock-trading-features-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="stock-trading-features-header"
          >
            <h2 className="stock-trading-features-title">
              Why Choose Our Stock Trading Platform?
            </h2>
            <p className="stock-trading-features-subtitle">
              Experience professional-grade stock trading with cutting-edge technology and competitive pricing.
            </p>
          </motion.div>

          <div className="stock-trading-features-grid">
            {stockFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="stock-trading-feature-card"
              >
                <div className="stock-trading-feature-icon">
                  {feature.icon}
                </div>
                <h3 className="stock-trading-feature-title">{feature.title}</h3>
                <p className="stock-trading-feature-description">{feature.description}</p>
                <ul className="stock-trading-feature-benefits">
                  {feature.benefits.map((benefit, idx) => (
                    <li key={idx} className="stock-trading-feature-benefit">
                      <div className="stock-trading-feature-check" />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Stocks Section */}
      <section className="stock-trading-stocks-section">
        <div className="stock-trading-stocks-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="stock-trading-stocks-header"
          >
            <h2 className="stock-trading-stocks-title">Popular Stocks to Trade</h2>
            <p className="stock-trading-stocks-subtitle">
              Start trading some of the most popular stocks in the market
            </p>
          </motion.div>

          <div className="stock-trading-stocks-grid">
            {popularStocks.map((stock, index) => (
              <motion.div
                key={stock.symbol}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="stock-trading-stock-card"
              >
                <div className="stock-trading-stock-header">
                  <div className="stock-trading-stock-symbol">{stock.symbol}</div>
                  <div className={`stock-trading-stock-change ${stock.change.startsWith('+') ? 'positive' : 'negative'}`}>
                    {stock.change}
                  </div>
                </div>
                <div className="stock-trading-stock-name">{stock.name}</div>
                <div className="stock-trading-stock-price">{stock.price}</div>
                <div className="stock-trading-stock-sector">{stock.sector}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="stock-trading-cta-section">
        <div className="stock-trading-cta-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="stock-trading-cta-content"
          >
            <h2 className="stock-trading-cta-title">
              Ready to Start Stock Trading?
            </h2>
            <p className="stock-trading-cta-subtitle">
              Join thousands of traders who trust ChartLine for their stock trading needs. 
              Open your account today and start trading with zero commissions.
            </p>
            <motion.a
              href="https://dashboard.chartlinebrokerage.com"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="stock-trading-cta-button"
            >
              Open Trading Account
            </motion.a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default StockTrading; 